<script lang="ts">
  import { onDestroy, onMount, type Snippet } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ProgressSteps, {
    type ProgressStep,
  } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import {
    completeDemoOrganizationReset,
    listDemoResetMembershipCandidates,
    type DemoDataResetMembershipCandidate,
    type DemoDataResetResponse,
  } from "../../../../../../../../../../../../api/kehrblatt/demo-data"
  import {
    LOCKED_WORKFLOW_STEP_MESSAGE,
    selectedWorkflowStepIndex,
    workflowStateForStep,
  } from "../../../../../../../../../../_shared/_logic/workflow-progress"
  import type {
    DemoResetMemberOutput,
    DemoResetMemberRole,
    DemoResetRunPhase,
    DemoResetStepId,
    DemoResetWorkflowEvent,
    DemoResetWorkflowOutputs,
  } from "./_shared/_logic/demo-data-reset-contract"
  import { applyDemoResetWorkflowEvent, completedDemoResetSteps } from "./_logic/transitions"
  import PrimaryStep from "./steps/a_select_primary"
  import SecondaryStep from "./steps/b_select_secondary"
  import ResetStep from "./steps/c_reset"

  type OverlayHostProps = {
    onremoveOverlay?: (() => void) | null
    onshowOverlay?: (() => void) | null
    reloadBrowser?: () => void
    setCloseDisabled?: ((disabled: boolean) => void) | null
    setActionButtonSnippet?: ((snippet: Snippet | null) => void) | null
  }

  const {
    organizationId,
    onResetDone,
    onremoveOverlay = null,
    onshowOverlay = null,
    reloadBrowser = () => window.location.reload(),
    setCloseDisabled = null,
    setActionButtonSnippet = null,
  }: {
    organizationId: string
    onResetDone: (result: DemoDataResetResponse) => void
  } & OverlayHostProps = $props()

  const stepOrder: DemoResetStepId[] = ["primary", "secondary", "reset"]
  let membersLoading = $state(true)
  let membersError = $state("")
  let candidates = $state<DemoDataResetMembershipCandidate[]>([])
  let outputs = $state<DemoResetWorkflowOutputs>({})
  let selectedStep = $state<DemoResetStepId>("primary")
  let runPhase = $state<DemoResetRunPhase>("ready")
  // The last choice survives editing an earlier step so the picker can preselect it again.
  let primaryDraftId = $state<string | null>(null)
  let secondaryDraftId = $state<string | null>(null)
  let membersController: AbortController | null = null

  const completedSteps = $derived(completedDemoResetSteps(outputs))
  const selectedIndex = $derived(
    selectedWorkflowStepIndex({ stepOrder, completedSteps, selectedStep }),
  )
  const navigationLocked = $derived(
    runPhase === "enqueueing" ||
      runPhase === "running" ||
      runPhase === "status_unavailable" ||
      runPhase === "succeeded_waiting_download" ||
      runPhase === "downloaded",
  )
  const footerDisabled = $derived(
    runPhase === "enqueueing" ||
      runPhase === "running" ||
      runPhase === "succeeded_waiting_download",
  )
  const footerLabel = $derived(runPhase === "ready" ? "Abbrechen" : "Schließen")

  const close = () => onremoveOverlay?.()

  // Memberships, sessions and credentials are preserved; only the demo data is reloaded.
  const completeSuccess = (result: DemoDataResetResponse) => {
    completeDemoOrganizationReset(organizationId, result)
    onResetDone(result)
    newNotification({ class: "success", html: "Testdaten wurden zurückgesetzt." })
    close()
    window.setTimeout(reloadBrowser, 300)
  }

  const requestClose = () => {
    if (footerDisabled) return

    if (runPhase === "downloaded" && outputs.reset) {
      completeSuccess(outputs.reset.result)

      return
    }

    close()
  }

  const applyEvent = (event: DemoResetWorkflowEvent) => {
    outputs = applyDemoResetWorkflowEvent(outputs, event)
  }

  const savePrimary = (output: DemoResetMemberOutput) => {
    primaryDraftId = output.membershipId

    if (secondaryDraftId === output.membershipId) secondaryDraftId = null

    applyEvent({ type: "member_selected", role: "primary", output })
    selectedStep = "secondary"
  }

  const saveSecondary = (output: DemoResetMemberOutput) => {
    secondaryDraftId = output.membershipId
    applyEvent({ type: "member_selected", role: "secondary", output })
    selectedStep = "reset"
  }

  const editMember = (role: DemoResetMemberRole) => {
    if (navigationLocked) return

    applyEvent({ type: "member_edit_requested", role })
    selectedStep = role
  }

  const handleJobStarted = () => {
    setCloseDisabled?.(true)
  }

  const handleRunPhase = (phase: DemoResetRunPhase) => {
    runPhase = phase
    setCloseDisabled?.(phase !== "ready" && phase !== "failed" && phase !== "status_unavailable")
  }

  const handleResetOutput = (result: DemoDataResetResponse) => {
    applyEvent({ type: "street_lists_downloaded", result })
  }

  const loadMembers = async () => {
    membersLoading = true
    membersError = ""
    const controller = new AbortController()
    membersController?.abort()
    membersController = controller
    try {
      const loaded = await listDemoResetMembershipCandidates(organizationId, {
        signal: controller.signal,
      })

      if (controller.signal.aborted) return

      candidates = loaded
    } catch (error) {
      if (controller.signal.aborted) return

      console.error("demo_reset_members_failed", error)
      membersError = "Die Mitglieder der Organisation konnten nicht geladen werden."
    } finally {
      if (membersController === controller) membersController = null

      if (!controller.signal.aborted) membersLoading = false
    }
  }

  const handleSelectedIndexChange = (index: number) => {
    const requested = stepOrder[index]

    if (!requested || navigationLocked) return

    if (requested === "secondary" && !outputs.primary) return

    if (requested === "reset" && (!outputs.primary || !outputs.secondary)) return

    selectedStep = requested
  }

  const rawSteps: ProgressStep[] = $derived([
    {
      id: "primary",
      label: "Chef",
      description: "Bestehendes Mitglied als Chef auswählen.",
      hideDefaultAction: true,
      snippet: primaryStep,
    },
    {
      id: "secondary",
      label: "Geselle",
      description: "Bestehendes Mitglied als Gesellen auswählen.",
      hideDefaultAction: true,
      snippet: secondaryStep,
    },
    {
      id: "reset",
      label: "Zurücksetzen",
      description: "Reset ausführen und beide ISI-Exporte herunterladen.",
      hideDefaultAction: true,
      snippet: resetStep,
    },
  ])
  const steps: ProgressStep[] = $derived(
    rawSteps.map((step) => ({
      ...step,
      workflowState: workflowStateForStep({
        step: step.id as DemoResetStepId,
        stepOrder,
        completedSteps,
      }),
    })),
  )

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(footerActions)
    setCloseDisabled?.(false)
    void loadMembers()
  })

  onDestroy(() => {
    membersController?.abort()
    setActionButtonSnippet?.(null)
    setCloseDisabled?.(false)
  })
</script>

<div class="demo-reset-overlay form light" data-testid="kehrblatt.demoReset.workflow">
  <ProgressSteps
    {steps}
    navigationMode="guarded"
    {selectedIndex}
    onSelectedIndexChange={handleSelectedIndexChange}
    lockedStepMessage={LOCKED_WORKFLOW_STEP_MESSAGE}
    showControls={false}
    darkMode={true}
    syncUrl={false}
  />
</div>

{#snippet primaryStep(ctx)}
  <PrimaryStep
    {ctx}
    {candidates}
    initialMembershipId={primaryDraftId}
    loading={membersLoading}
    loadError={membersError}
    onRetry={loadMembers}
    output={outputs.primary ?? null}
    onOutput={savePrimary}
    onEdit={() => editMember("primary")}
  />
{/snippet}

{#snippet secondaryStep(ctx)}
  {#if outputs.primary}
    <SecondaryStep
      {ctx}
      {candidates}
      primaryMembershipId={outputs.primary.membershipId}
      initialMembershipId={secondaryDraftId}
      output={outputs.secondary ?? null}
      onOutput={saveSecondary}
      onEdit={() => editMember("secondary")}
    />
  {/if}
{/snippet}

{#snippet resetStep(ctx)}
  {#if outputs.primary && outputs.secondary}
    <ResetStep
      {ctx}
      {organizationId}
      primary={outputs.primary}
      secondary={outputs.secondary}
      onJobStarted={handleJobStarted}
      onPhaseChange={handleRunPhase}
      onOutput={handleResetOutput}
    />
  {/if}
{/snippet}

{#snippet footerActions()}
  <CTA
    type="secondaryButton"
    content={footerLabel}
    onClick={requestClose}
    disabled={footerDisabled}
  />
{/snippet}

<style lang="less">
  .demo-reset-overlay {
    color: var(--text-100);
    padding-bottom: 1rem;
  }

  .demo-reset-overlay :global(p) {
    margin: 0;
  }
</style>
